/**
 * IaPromoLinkBuilder — builds a native Shopify cart permalink in the browser.
 *
 * Output shape: {base}/cart/{variant}:{qty}(,{variant}:{qty})?discount=…&storefront=true
 *   - One or more line items (variant ID + quantity).
 *   - Optional discount code(s), comma-separated.
 *   - Destination: checkout (default) | cart page (?storefront=true) | Shop Pay (?payment=shop_pay).
 *
 * Pure client-side: no network requests, nothing is sent anywhere.
 */
class IaPromoLinkBuilder extends HTMLElement {
  connectedCallback() {
    this.baseUrl = (this.dataset.baseUrl || '').replace(/\/+$/, '');
    this.copyLabel = this.dataset.copyLabel || 'Copy link';
    this.copySuccessLabel = this.dataset.copySuccessLabel || 'Copied!';
    this.emptyHint = this.dataset.emptyHint || '';

    this.rows = this.querySelector('[data-rows]');
    this.rowTemplate = this.querySelector('[data-row-template]');
    this.discountInput = this.querySelector('[data-discount]');
    this.destinationInput = this.querySelector('[data-destination]');
    this.output = this.querySelector('[data-output]');
    this.copyButton = this.querySelector('[data-copy]');
    this.addButton = this.querySelector('[data-add]');

    // Event delegation: any input/change inside the component rebuilds the link.
    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleInput);
    this.addEventListener('click', this.handleClick);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleInput);
    this.removeEventListener('click', this.handleClick);
  }

  handleInput = () => {
    this.render();
  };

  handleClick = (event) => {
    if (event.target.closest('[data-add]')) {
      this.addRow();
    } else if (event.target.closest('[data-remove]')) {
      this.removeRow(event.target.closest('[data-row]'));
    } else if (event.target.closest('[data-copy]')) {
      this.copyLink();
    }
  };

  addRow() {
    const fragment = this.rowTemplate.content.cloneNode(true);
    this.rows.appendChild(fragment);
    this.rows.lastElementChild?.querySelector('[data-variant]')?.focus();
    this.render();
  }

  removeRow(row) {
    if (!row) return;
    // Always keep at least one row so the tool is never empty.
    if (this.rows.querySelectorAll('[data-row]').length <= 1) {
      row.querySelectorAll('input').forEach((input) => {
        input.value = input.dataset.qty !== undefined ? '1' : '';
      });
    } else {
      row.remove();
    }
    this.render();
  }

  /**
   * Pull the variant ID out of a raw value. Accepts a bare number, a variant
   * GID (gid://shopify/ProductVariant/123) or a pasted admin/storefront URL —
   * the variant ID is always the last run of digits.
   */
  parseVariantId(value) {
    const matches = String(value).match(/\d+/g);
    return matches ? matches[matches.length - 1] : '';
  }

  collectLineItems() {
    const items = [];
    this.rows.querySelectorAll('[data-row]').forEach((row) => {
      const id = this.parseVariantId(row.querySelector('[data-variant]')?.value || '');
      if (!id) return;
      const qty = Math.max(1, parseInt(row.querySelector('[data-qty]')?.value, 10) || 1);
      items.push(`${id}:${qty}`);
    });
    return items;
  }

  buildLink() {
    const items = this.collectLineItems();
    if (!items.length) return '';

    let url = `${this.baseUrl}/cart/${items.join(',')}`;
    const params = [];

    const discount = (this.discountInput?.value || '').trim();
    if (discount) {
      const codes = discount
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean)
        .map((code) => encodeURIComponent(code))
        .join(',');
      if (codes) params.push(`discount=${codes}`);
    }

    const destination = this.destinationInput?.value;
    if (destination === 'cart') {
      params.push('storefront=true');
    } else if (destination === 'shop_pay') {
      params.push('payment=shop_pay');
    }

    if (params.length) url += `?${params.join('&')}`;
    return url;
  }

  render() {
    this.link = this.buildLink();
    const hasLink = Boolean(this.link);

    this.output.textContent = hasLink ? this.link : this.emptyHint;
    this.output.classList.toggle('ia-plb__link--empty', !hasLink);

    this.copyButton.disabled = !hasLink;
    if (this.copyButton.textContent !== this.copyLabel) {
      this.copyButton.textContent = this.copyLabel;
    }
  }

  async copyLink() {
    if (!this.link) return;
    try {
      await navigator.clipboard.writeText(this.link);
    } catch (error) {
      // Fallback for non-secure contexts / older browsers.
      const helper = document.createElement('textarea');
      helper.value = this.link;
      helper.setAttribute('readonly', '');
      helper.style.position = 'absolute';
      helper.style.left = '-9999px';
      this.appendChild(helper);
      helper.select();
      document.execCommand('copy');
      helper.remove();
    }

    this.copyButton.textContent = this.copySuccessLabel;
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      this.copyButton.textContent = this.copyLabel;
    }, 2000);
  }
}

customElements.define('ia-promo-link-builder', IaPromoLinkBuilder);
